import React, { useMemo } from 'react'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LoadingButton from '@app/src/components/LoadingButton'
import type { OverrideExtrasRenderer } from './FieldToggleChips'
import FieldToggleChips from './FieldToggleChips'
import MappingPreviewTable from './MappingPreviewTable'
import type { FieldMappingDto, PreviewRow } from '@app/src/types/tabularImport'
import { sortMappingsByActive, hasRequiredMappings, getMissingRequiredMappings } from '../mappingLogic'
import { getFieldConfig } from '../fieldConfig'
import { useTabularImportEntity } from '../TabularImportEntityContext'

export type MappingStepProps = {
  fieldMappings: FieldMappingDto[]
  previewRows: PreviewRow[]
  totalRows: number
  isApproving: boolean
  onToggleMapping: (mappingId: number) => void
  onRetargetMapping: (mappingId: number, targetField: string) => void
  onSplitMapping: (mappingId: number) => void
  onApprove: () => void
  onCancel: () => void
  children?: React.ReactNode
  categoryColumns?: string[]
  unassignedHint?: string
  renderOverrideExtras?: OverrideExtrasRenderer
}

const DEFAULT_UNASSIGNED_HINT = 'Unassigned column. Map it to a field to include it.'

const MappingStep: React.FC<MappingStepProps> = ({
  fieldMappings,
  previewRows,
  totalRows,
  isApproving,
  onToggleMapping,
  onRetargetMapping,
  onSplitMapping,
  onApprove,
  onCancel,
  children,
  categoryColumns,
  unassignedHint = DEFAULT_UNASSIGNED_HINT,
  renderOverrideExtras,
}) => {
  const { fieldConfigs, requiredFields } = useTabularImportEntity()
  const sortedMappings = useMemo(() => sortMappingsByActive(fieldMappings), [fieldMappings])
  const hasRequired = useMemo(() => hasRequiredMappings(fieldMappings, requiredFields), [fieldMappings, requiredFields])
  const missingRequired = useMemo(
    () => getMissingRequiredMappings(fieldMappings, requiredFields),
    [fieldMappings, requiredFields],
  )
  const activeFields = useMemo(
    () => sortedMappings.filter(m => m.isApplied).map(m => m.targetFieldName),
    [sortedMappings],
  )
  // Keep emptied mappings (no extraction tree, e.g. a field freed by a split) out of the
  // preview — they'd otherwise render as a column of em-dashes. Unassigned placeholder
  // columns still have a tree, so they keep showing their raw values.
  const disabledFields = useMemo(
    () => sortedMappings.filter(m => !m.isApplied && m.extractionTree !== null).map(m => m.targetFieldName),
    [sortedMappings],
  )
  // Only count columns actually matched to a real field — unassigned placeholder
  // columns carry a tree but aren't matched to anything yet.
  const matchedCount = useMemo(
    () =>
      fieldMappings.filter(m => m.extractionTree !== null && getFieldConfig(fieldConfigs, m.targetFieldName)).length,
    [fieldMappings, fieldConfigs],
  )
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Column matches
        </Typography>
        <Typography variant="body1" color="text.secondary">
          We analysed your file and matched {matchedCount} columns to Worldfavor fields. Review the preview and disable
          any fields that don&apos;t look right.
        </Typography>
      </Box>
      {missingRequired.length > 0 && (
        <Alert severity="error" icon={<ErrorOutlineIcon />}>
          Required field{missingRequired.length > 1 ? 's' : ''} missing: {missingRequired.join(', ')}. This field is
          mandatory for importing. Please ensure your file has a column with this data and try again.
        </Alert>
      )}
      {fieldMappings.length > 0 && (
        <FieldToggleChips
          mappings={sortedMappings}
          missingRequiredFields={missingRequired}
          onToggle={onToggleMapping}
          onRetarget={onRetargetMapping}
          onSplit={onSplitMapping}
          unassignedHint={unassignedHint}
          renderOverrideExtras={renderOverrideExtras}
        />
      )}
      {previewRows.length > 0 && (
        <MappingPreviewTable
          previewRows={previewRows}
          activeFields={activeFields}
          disabledFields={disabledFields}
          totalRows={totalRows}
          categoryColumns={categoryColumns}
        />
      )}
      {fieldMappings.length === 0 && (
        <Alert severity="warning">No mappings detected. Please check your file format or contact support.</Alert>
      )}
      {children}
      <Box display="flex" justifyContent="space-between">
        <Button onClick={onCancel}>Cancel</Button>
        <LoadingButton
          loading={isApproving}
          variant="contained"
          onClick={onApprove}
          disabled={fieldMappings.length === 0 || !hasRequired}
        >
          Apply matches &amp; continue
        </LoadingButton>
      </Box>
    </Stack>
  )
}

export default MappingStep
